import _ from 'lodash'
import { createFedopsLogger } from '@wix/thunderbolt-commons'
import type { ViewerPlatformEssentials } from '@wix/fe-essentials-viewer-platform'
import type { PlatformServicesAPI, PlatformEnvData, SessionServiceAPI } from '@wix/thunderbolt-symbols'
import type { BsiManager } from '../../bsiManagerModule'
import { biFactory } from './bi'
import { monitoringFactory } from './monitoring'
import { platformBiLoggerFactory } from '../../bi/biLoggerFactory'

export const createPlatformAppServicesApi = ({
	platformEnvData: {
		bi: biData,
		document: { referrer },
		location,
		site,
		topology,
	},
	appDefinitionId,
	instanceId,
	csrfToken,
	bsiManager,
	sessionService,
	essentials,
}: {
	platformEnvData: PlatformEnvData
	appDefinitionId: string
	instanceId: string
	csrfToken: string
	bsiManager: BsiManager
	sessionService: SessionServiceAPI
	essentials: ViewerPlatformEssentials
}): PlatformServicesAPI => {
	const viewMode = biData.isPreview ? ('preview' as const) : ('site' as const)

	const biLoggerFactoriesCreator = platformBiLoggerFactory({ sessionService, biData, location, site, factory: essentials.biLoggerFactory })
	const biLoggerFactory = () =>
		biLoggerFactoriesCreator.createBaseBiLoggerFactory().updateDefaults({
			_appId: appDefinitionId,
			_instanceId: instanceId,
			bsi: () => bsiManager.getBsi(),
		})

	const fedOpsLoggerFactory = createFedopsLogger({
		biLoggerFactory: biLoggerFactoriesCreator.createBiLoggerFactoryForFedops().updateDefaults({
			_appId: appDefinitionId,
			_instanceId: instanceId,
			bsi: () => bsiManager.getBsi(), // TODO PLAT-1602 do not extend bsi upon fedops reports
		}),
		customParams: {
			isMobileFriendly: biData.isMobileFriendly,
			viewerName: 'thunderbolt',
			viewMode,
		},
		paramsOverrides: { is_rollout: biData.rolloutData.isTBRollout },
		factory: essentials.createFedopsLogger,
	})

	const bi = biFactory({ biData, metaSiteId: location.metaSiteId, viewMode, sessionService })
	const monitoring = monitoringFactory({ url: biData.pageData.pageUrl, viewMode, viewerVersion: biData.viewerVersion, referrer })
	const appEssentials = essentials.createAppEssentials({
		appDefId: appDefinitionId,
		getLoggerForWidget: fedOpsLoggerFactory.getLoggerForWidget.bind(fedOpsLoggerFactory),
		biLoggerFactory,
	})

	return {
		getCsrfToken: () => csrfToken,
		bi,
		biLoggerFactory,
		fedOpsLoggerFactory,
		reportTrace: _.noop,
		monitoring,
		essentials: appEssentials,
		topology,
	}
}
